import { z } from 'zod';

import { MAX_WEEKDAY, WEEK_LENGTH } from '../domain/calendar/week.js';
import {
  dateKeySchema,
  idempotencyKeySchema,
  recordIdSchema,
  versionSchema,
} from './common.schema.js';

// Three parts of the day rather than AM/PM: a supplement regimen splits by purpose as much
// as by clock time — fat-soluble vitamins with breakfast, a probiotic away from other
// capsules at lunch, magnesium before bed — and two buckets could not tell a morning dose
// from a midday one.
export const vitaminTimeSchema = z.enum(['morning', 'afternoon', 'evening']);
export const VITAMIN_TIME_ORDER = Object.freeze(['morning', 'afternoon', 'evening']);
export const VITAMIN_TIME_LABELS = Object.freeze({ morning: 'Morning', afternoon: 'Afternoon', evening: 'Evening' });

/**
 * The weekdays a vitamin repeats on, `0` for Sunday.
 *
 * Stored on the vitamin rather than derived from past adherence, so "every Monday and
 * Thursday" is something the user stated and can change, not something the app inferred
 * from two ticks and then kept acting on.
 */
const repeatDaysSchema = z
  .array(z.number().int().min(0, 'Not a weekday').max(MAX_WEEKDAY, 'Not a weekday'))
  .max(WEEK_LENGTH, 'A week has seven days');

/**
 * Where to buy or read more about a vitamin — a brand's product page, a store listing,
 * whatever the user already trusts for that particular supplement. A vitamin can have more
 * than one (the same supplement from two different brands, or a store link alongside the
 * brand's own page), so this lives as a short list rather than a single field.
 */
const vitaminLinkSchema = z.strictObject({
  label: z.string().trim().min(1, 'Enter a label').max(80),
  url: z.string().trim().url('Enter a full link, starting with https://').max(2000),
});
const vitaminLinksSchema = z.array(vitaminLinkSchema).max(5, 'Add at most 5 links');

const vitaminFields = {
  name: z.string().trim().min(1, 'Enter a vitamin name').max(80),
  dose: z.string().trim().min(1, 'Enter a dose').max(40),
  time: vitaminTimeSchema,
};

export const createVitaminRequestSchema = z.strictObject({
  ...vitaminFields,
  repeatDays: repeatDaysSchema.default([]),
  links: vitaminLinksSchema.default([]),
  idempotencyKey: idempotencyKeySchema,
});

/**
 * `repeatDays` and `links` are optional on update and absent means "leave it alone", not
 * "clear it". The library form's edit dialog does not always round-trip every field, so
 * defaulting either here would have a plain dose edit silently forget which days a vitamin
 * repeats on, or wipe out its links.
 */
export const updateVitaminRequestSchema = z.strictObject({
  ...vitaminFields,
  repeatDays: repeatDaysSchema.optional(),
  links: vitaminLinksSchema.optional(),
  version: versionSchema,
});

export const vitaminParamsSchema = z.strictObject({ vitaminId: recordIdSchema });
export const vitaminDayParamsSchema = z.strictObject({
  vitaminId: recordIdSchema,
  dateKey: dateKeySchema,
});
export const vitaminDayQuerySchema = z.strictObject({ dateKey: dateKeySchema });

/**
 * How many dates one adherence write may touch.
 *
 * A week's repeat needs seven. The cap is a month so the endpoint is not week-shaped for
 * no reason, while still bounding what a single request can write.
 */
export const MAX_ADHERENCE_DATES = 31;

const adherenceDateListSchema = z
  .array(dateKeySchema)
  .max(MAX_ADHERENCE_DATES, `Choose at most ${MAX_ADHERENCE_DATES} dates`);

/**
 * Set adherence for several dates at once.
 *
 * Both sides are explicit. A single `dateKeys` list would have had to imply a range to
 * decide what "not in the list" means, and the answer differs between "log these three
 * days" and "these three are the whole week now". Naming what is cleared removes the
 * guess, and makes an accidental delete impossible to write by omission.
 */
export const vitaminAdherenceBulkSchema = z
  .strictObject({
    taken: adherenceDateListSchema.default([]),
    cleared: adherenceDateListSchema.default([]),
  })
  .refine((body) => body.taken.length + body.cleared.length > 0, {
    message: 'Choose at least one date',
  });

/**
 * Adherence for several dates, and optionally a new weekly repeat on the vitamin
 * itself, applied as one write. `repeatDays` absent means "just the dates", the same
 * distinction `updateVitaminRequestSchema` draws for the same field: leave it alone,
 * not clear it. `version` is required regardless, since the caller always has it and
 * it is only consulted when `repeatDays` is actually present.
 */
export const vitaminSetDaysSchema = z
  .strictObject({
    taken: adherenceDateListSchema.default([]),
    cleared: adherenceDateListSchema.default([]),
    repeatDays: repeatDaysSchema.optional(),
    version: versionSchema,
  })
  .refine((body) => body.taken.length + body.cleared.length > 0 || body.repeatDays !== undefined, {
    message: 'Choose at least one date, or a new weekly repeat',
  });

/**
 * Whole days between two date keys, or `NaN` if either is not a real calendar date.
 *
 * `Date.parse` rather than the shared anchor helper on purpose: the anchor throws on
 * "2026-02-31", which passes the shape check above, and a throw inside a refine is a 500
 * where this needs to be a 400. `NaN` fails the comparison and reports the range as too
 * wide, which is the right answer for an input that names no range at all.
 */
function daysApart(from, to) {
  return Math.round((Date.parse(`${to}T00:00:00Z`) - Date.parse(`${from}T00:00:00Z`)) / 86_400_000);
}

/**
 * Read adherence across a range, so a week of chips can be drawn from one request rather
 * than seven.
 */
export const vitaminAdherenceRangeQuerySchema = z
  .strictObject({ from: dateKeySchema, to: dateKeySchema })
  // Date keys are zero padded, so lexical order is calendar order.
  .refine(({ from, to }) => from <= to, { message: 'The range ends before it starts' })
  .refine(({ from, to }) => daysApart(from, to) <= MAX_ADHERENCE_DATES - 1, {
    message: `Ask for at most ${MAX_ADHERENCE_DATES} days at a time`,
  });
